import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
//
import { setSession, clearClientSession } from './utils';
import api from '../services/api';

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const logout = useCallback(async () => {
    try {
      await api.post('/association/logout');
    } catch (error) {
      // Cookie clear may still succeed server-side; always wipe client state
      console.error(error);
    }
    clearClientSession();
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const initialize = useCallback(async () => {
    // Migrate away from legacy JWT-in-sessionStorage (AF-03)
    sessionStorage.removeItem('accessToken');

    try {
      const response = await api.get('/association/me');
      const { user } = response.data;
      const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;

      if (isEmpty(user) || !user?._id) {
        clearClientSession();
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
        return;
      }

      setSession(user._id);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: true,
          user: {
            ...user,
            role: 'association',
          },
        },
      });
    } catch (error) {
      clearClientSession();
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback(async (username, password) => {
    const response = await api.post('/association/login', {
      username,
      password,
    });

    const { user, id, accessToken } = response.data;
    if (accessToken) {
      sessionStorage.setItem('devAccessToken', accessToken);
    } else {
      sessionStorage.removeItem('devAccessToken');
    }
    setSession(user?._id || id);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          ...user,
          role: 'association',
        },
      },
    });

    window.location.reload(true);
  }, []);

  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await api.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { user } = response.data;
    setSession(user?._id);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      register,
      logout,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, logout, register]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
